// API client — all calls go through here
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem("auditpros_user");
    if (saved) {
      const parsed = JSON.parse(saved);
      const account = parsed?.account;
      const email = account?.idTokenClaims?.email
        || account?.idTokenClaims?.preferred_username
        || account?.idTokenClaims?.emails?.[0]
        || account?.username || account?.preferred_username || account?.email;
      const name = account?.name
        || account?.idTokenClaims?.name
        || account?.idTokenClaims?.given_name
        || account?.username?.split('@')[0] || "";
      const viewMode = localStorage.getItem("auditpros_view_mode") || "ca";
      const headers = {};
      if (email) headers["x-user-email"] = email;
      if (name) headers["x-user-name"] = name;
      if (viewMode) headers["x-view-mode"] = viewMode;
      return headers;
    }
  } catch { /* ignore */ }
  return {};
}

// apiFetch now accepts optional syncRunId as third param
// When provided it is appended as &syncRunId=... so the backend
// can query the specific sync instead of the latest one.
export async function apiFetch(path, sourceId, syncRunId) {
  let url = `${BASE}${path}`;
  const sep = path.includes('?') ? '&' : '?';
  if (sourceId) url += `${sep}sourceId=${sourceId}`;
  if (syncRunId) url += `${sourceId ? '&' : sep}syncRunId=${syncRunId}`;
  const res = await fetch(url, { headers: { "Content-Type": "application/json", ...getAuthHeaders() } });
  if (!res.ok) throw new Error(`HTTP ${res.status} — ${path}`);
  return res.json();
}

export async function apiPost(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    // Prefer the server's error message, then detail, then a generic fallback
    throw new Error(err.error || err.detail || `HTTP ${res.status} — ${path}`);
  }
  return res.json();
}

export async function apiPatch(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || `HTTP ${res.status}`); }
  return res.json();
}

export async function apiDelete(path) {
  const res = await fetch(`${BASE}${path}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || `HTTP ${res.status}`); }
  return res.json();
}

export const api = {
  // Public contact form (no auth)
  submitContactQuery: (payload) => apiPost('/contact', payload),
  // User profile
  getMe: () => apiFetch('/users/me'),
  submitIcai: (icaiMemberId) => apiPost('/users/icai', { icaiMemberId }),
  // Team
  getTeam: () => apiFetch('/users/team'),
  inviteTeamMember: (email) => apiPost('/users/team/invite', { invitedEmail: email }),
  acceptInvite: (id) => apiPost(`/users/team/accept/${id}`, {}),
  rejectInvite: (id) => apiPost(`/users/team/reject/${id}`, {}),
  // Sources
  sources: () => apiFetch('/sources'),
  syncHistory: (sid) => apiFetch('/sync/history', sid),
  // AI report
  getAiReport: (syncRunId) => apiFetch(`/ai-report/${syncRunId}`),
  generateAiReport: (syncRunId) => apiPost(`/ai-report/${syncRunId}/generate`, {}),
  // Data endpoints — all accept optional syncRunId
  auditReport: (sid, rid) => apiFetch('/audit-report', sid, rid),
  auditSummary: (sid, type, rid) => apiFetch(type ? `/audit-summary?type=${type}` : '/audit-summary', sid, rid),
  statutory: (sid, rid) => apiFetch('/statutory-check', sid, rid),
  receivables: (sid, rid) => apiFetch('/receivables', sid, rid),
  outstandingPay: (sid, rid) => apiFetch('/outstanding-payables', sid, rid),
  grossPayables: (sid, rid) => apiFetch('/gross-payables', sid, rid),
  trialBalance: (sid, rid) => apiFetch('/trial-balance', sid, rid),
  salesRegister: (sid, rid) => apiFetch('/sales-register', sid, rid),
  purchaseRegister: (sid, rid) => apiFetch('/purchase-register', sid, rid),
  gstInvoices: (sid, rid) => apiFetch('/gst-invoices', sid, rid),
  bankBook: (sid, rid) => apiFetch('/bank-book', sid, rid),
  unbookedPayments: (sid, rid) => apiFetch('/unbooked-payments', sid, rid),
  balanceCheck: (sid, rid) => apiFetch('/balance-sheet-check', sid, rid),
  // Customers
  customers: () => apiFetch('/customers'),
  customerDetail: (id) => apiFetch(`/customers/${id}`),
  customerSummary: () => apiFetch('/customers/summary'),
  unlinkedSources: () => apiFetch('/customers/sources/unlinked'),
  licenseSummary: () => apiFetch('/customers/licenses/summary'),
  // Subscriptions
  getSubscriptionPlans:  () => apiFetch('/subscriptions/plans'),
  getMySubscription:     () => apiFetch('/subscriptions/me'),
  updateSubscription:    (planCode, unitCount) => apiPost('/subscriptions/me', { plan_code: planCode, unit_count: unitCount }),
  // Tally Bridge key management
  getSourceKeyInfo:      (sourceId) => apiFetch(`/sources/${sourceId}/key-info`),
  generateSourceApiKey:  (sourceId) => apiPost(`/sources/generate-key/${sourceId}`, {}),
  revokeSourceApiKey:    (sourceId) => apiDelete(`/sources/${sourceId}/key`),
  // Engagements
  listEngagements: ({ page = 1, limit = 20, status = '', search = '' } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (status) p.set('status', status);
    if (search) p.set('search', search);
    return apiFetch(`/engagements?${p}`);
  },
  getEngagement:          (id) => apiFetch(`/engagements/${id}`),
  createEngagement:       (body) => apiPost('/engagements', body),
  updateEngagementStatus: (id, status, comment) => apiPatch(`/engagements/${id}/status`, { status, comment }),
  deleteEngagement:       (id) => apiDelete(`/engagements/${id}`),
  listMyEngagements:      () => apiFetch('/engagements/mine'),
  getMyEngagement:        (id) => apiFetch(`/engagements/mine/${id}`),
  listEngagementComments: (id) => apiFetch(`/engagements/${id}/comments`),
  postEngagementComment:  (id, body) => apiPost(`/engagements/${id}/comments`, { body }),
  // Managed customer self-migration
  inviteCustomer:         (customerId) => apiPost(`/customers/${customerId}/invite`, {}),
  claimByToken:           (token) => apiPost(`/customers/claim/${token}`, {}),
  selfClaimInit:          () => apiPost('/customers/self-claim/init', {}),
  selfClaimVerifyOtp:     (customerId, otp) => apiPost('/customers/self-claim/verify-otp', { customerId, otp }),
  // Empanelled CAs (admin only)
  listEmpanelledCAs:      (search) => apiFetch(`/empanelled-cas${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  searchEmpanelableCAs:   (search) => apiFetch(`/empanelled-cas/search-cas${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  addEmpanelledCA:        (userId) => apiPost('/empanelled-cas', { user_id: userId }),
  removeEmpanelledCA:     (id) => apiDelete(`/empanelled-cas/${id}`),
  // CA reviews
  getMyCAReview:      (caUserId) => apiFetch(`/ca-reviews/my/${caUserId}`),
  getReceivedReviews: () => apiFetch('/ca-reviews/received'),
  submitCAReview:     (body) => apiPost('/ca-reviews', body),
  updateCAResponse:   (id, responseText) => apiPatch(`/ca-reviews/${id}/response`, { response_text: responseText }),
  // Engagement requests
  createEngagementRequest:  (body) => apiPost('/engagement-requests', body),
  listEngagementRequests:   (status) => apiFetch(`/engagement-requests${status ? `?status=${status}` : ''}`),
  getEngagementRequest:     (id) => apiFetch(`/engagement-requests/${id}`),
  assignEngagementRequest:  (id, body) => apiPatch(`/engagement-requests/${id}/assign`, body),
  rejectEngagementRequest:  (id, note) => apiPatch(`/engagement-requests/${id}/reject`, { admin_note: note }),
  // Admin user directory
  getAllUsers: ({ page = 1, limit = 50, role = '', search = '' } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (role)   p.set('role',   role);
    if (search) p.set('search', search);
    return apiFetch(`/users/all?${p}`);
  },
};